import { OVERLAY_WIDTHS, SHEET_WIDTHS } from './chunk-SKYNNFFU.js';
import { useFullscreenPortal } from './chunk-MBTLYAMU.js';
import { cn } from './chunk-FFX3LZW7.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React4 from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, X, ChevronLeft } from 'lucide-react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("animate-pulse rounded-md bg-muted", className), ...props });
}
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuSeparator = React4.forwardRef(function DropdownMenuSeparator2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Separator,
    {
      ref,
      className: cn("-mx-1 my-1 h-px bg-border", className),
      ...props
    }
  );
});
var DropdownMenuContent = React4.forwardRef(function DropdownMenuContent2({ className, size = "auto", sideOffset = 4, collisionPadding = 8, ...props }, ref) {
  const portalContainer = useFullscreenPortal();
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Content,
    {
      ref,
      sideOffset,
      collisionPadding,
      className: cn(
        "z-50 max-w-[min(calc(100vw-1rem),var(--radix-dropdown-menu-content-available-width,100vw))] [overflow-wrap:anywhere] min-w-[8rem] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out",
        OVERLAY_WIDTHS[size],
        className
      ),
      ...props
    }
  ) });
});
var DropdownMenuItem = React4.forwardRef(function DropdownMenuItem2({ className, inset, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        inset && "pl-8",
        className
      ),
      ...props
    }
  );
});
var DropdownMenuCheckboxItem = React4.forwardRef(function DropdownMenuCheckboxItem2({ className, children, checked, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    DropdownMenuPrimitive.CheckboxItem,
    {
      ref,
      checked,
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "size-4" }) }) }),
        children
      ]
    }
  );
});
function DropdownMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Label,
    {
      className: cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className),
      ...props
    }
  );
}
var Sheet = SheetPrimitive.Root;
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
function SheetPortal({ container, ...props }) {
  const fullscreen = useFullscreenPortal();
  return /* @__PURE__ */ jsx(SheetPrimitive.Portal, { container: container === void 0 ? fullscreen : container, ...props });
}
var SheetOverlay = React4.forwardRef(function SheetOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SheetPrimitive.Overlay,
    {
      ref,
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
});
var sheetVariants = cva(
  "fixed z-50 max-h-dvh overflow-y-auto gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b",
        bottom: "inset-x-0 bottom-0 border-t",
        left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm"
      }
    },
    defaultVariants: { side: "right" }
  }
);
var SheetContent = React4.forwardRef(function SheetContent2({ side = "right", size = "default", className, children, overlay = true, ...props }, ref) {
  return /* @__PURE__ */ jsxs(SheetPortal, { children: [
    overlay ? /* @__PURE__ */ jsx(SheetOverlay, {}) : null,
    /* @__PURE__ */ jsxs(SheetPrimitive.Content, { ref, className: cn(sheetVariants({ side }), SHEET_WIDTHS[size], className), ...props, children: [
      children,
      /* @__PURE__ */ jsxs(SheetPrimitive.Close, { className: "absolute right-3 top-3 flex size-6 items-center justify-center rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-ring", children: [
        /* @__PURE__ */ jsx(X, { className: "size-4" }),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
      ] })
    ] })
  ] });
});
function SheetHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-y-2 text-center sm:text-left", className), ...props });
}
function SheetFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className), ...props });
}
var SheetTitle = React4.forwardRef(function SheetTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(SheetPrimitive.Title, { ref, className: cn("text-lg font-semibold text-foreground", className), ...props });
});
var SheetDescription = React4.forwardRef(function SheetDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(SheetPrimitive.Description, { ref, className: cn("text-sm text-muted-foreground", className), ...props });
});
var WIDTH_CLASS = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl"
};
function DrilldownPanel({
  open,
  onOpenChange,
  title,
  eyebrow,
  subtitle,
  headerRight,
  onBack,
  backLabel = "Back",
  contentKey,
  returnFocusRef,
  footer,
  side = "right",
  width = "lg",
  docked = false,
  className,
  children
}) {
  const titleRef = React4.useRef(null);
  const bodyRef = React4.useRef(null);
  const openedPanel = React4.useRef(null);
  React4.useEffect(() => {
    if (!open || contentKey === void 0) return;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    titleRef.current?.focus({ preventScroll: true });
  }, [open, contentKey]);
  return /* @__PURE__ */ jsx(Sheet, { open, onOpenChange, modal: !docked, children: /* @__PURE__ */ jsxs(
    SheetContent,
    {
      side,
      overlay: !docked,
      "aria-modal": docked ? void 0 : true,
      ...!subtitle ? { "aria-describedby": void 0 } : {},
      onOpenAutoFocus: (event) => {
        event.preventDefault();
        openedPanel.current = titleRef.current?.closest('[role="dialog"]') ?? null;
        titleRef.current?.focus({ preventScroll: true });
      },
      onCloseAutoFocus: returnFocusRef ? (event) => {
        event.preventDefault();
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement && active.closest('[role="dialog"]') !== openedPanel.current) return;
        returnFocusRef.current?.focus({ preventScroll: true });
      } : void 0,
      ...docked ? { onInteractOutside: (event) => event.preventDefault() } : {},
      className: cn("flex w-full flex-col gap-0 p-0", WIDTH_CLASS[width], className),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 border-b p-4 pr-12", children: [
          onBack && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: onBack,
              "aria-label": backLabel,
              title: backLabel,
              className: "mt-0.5 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
              children: /* @__PURE__ */ jsx(ChevronLeft, { className: "size-4" })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            eyebrow && /* @__PURE__ */ jsx("div", { className: "text-2xs font-medium uppercase tracking-wide text-muted-foreground", children: eyebrow }),
            /* @__PURE__ */ jsx(SheetTitle, { ref: titleRef, tabIndex: -1, className: "truncate text-base outline-none", children: title }),
            subtitle && /* @__PURE__ */ jsx(SheetDescription, { className: "truncate text-xs", children: subtitle })
          ] }),
          headerRight && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: headerRight })
        ] }),
        /* @__PURE__ */ jsx("div", { ref: bodyRef, className: "min-h-0 flex-1 overflow-y-auto p-4", children }),
        footer && /* @__PURE__ */ jsx("div", { className: "flex items-center justify-end gap-2 border-t bg-background p-4", children: footer })
      ]
    }
  ) });
}
var gaps = { compact: "gap-2", normal: "gap-3", spacious: "gap-4" };
var ResponsiveRow = React4.forwardRef(
  function ResponsiveRow2({ asChild, density = "normal", className, ...props }, ref) {
    const Component = asChild ? Slot : "div";
    return /* @__PURE__ */ jsx(Component, { ref, "data-slot": "responsive-row", className: cn("flex min-w-0 max-w-full flex-wrap items-center justify-between", gaps[density], className), ...props });
  }
);
var RowContent = React4.forwardRef(function RowContent2({ asChild, className, ...props }, ref) {
  const Component = asChild ? Slot : "div";
  return /* @__PURE__ */ jsx(Component, { ref, "data-slot": "row-content", className: cn("min-w-0 max-w-full grow basis-64", className), ...props });
});
var ActionGroup = React4.forwardRef(function ActionGroup2({ asChild, className, ...props }, ref) {
  const Component = asChild ? Slot : "div";
  return /* @__PURE__ */ jsx(Component, { ref, "data-slot": "action-group", className: cn("flex min-w-0 max-w-full flex-wrap items-center gap-2", className), ...props });
});
var ContentPanel = React4.forwardRef(
  function ContentPanel2({ asChild, density = "normal", width = "full", className, ...props }, ref) {
    const Component = asChild ? Slot : "div";
    return /* @__PURE__ */ jsx(Component, { ref, "data-slot": "content-panel", className: cn(
      "min-w-0 max-w-full rounded-md border bg-card text-card-foreground",
      density === "compact" ? "space-y-3 p-3" : "space-y-4 p-4",
      width === "compact" ? "w-80" : "w-full",
      className
    ), ...props });
  }
);
var ContentLabel = React4.forwardRef(
  function ContentLabel2({ size = "md", className, ...props }, ref) {
    return /* @__PURE__ */ jsx("span", { ref, className: cn("min-w-0 truncate", { sm: "max-w-40", md: "max-w-48", lg: "max-w-56" }[size], className), ...props });
  }
);

export { ActionGroup, ContentLabel, ContentPanel, DrilldownPanel, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, ResponsiveRow, RowContent, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetPortal, SheetTitle, SheetTrigger, Skeleton };
//# sourceMappingURL=chunk-6M2Q6PPL.js.map
//# sourceMappingURL=chunk-6M2Q6PPL.js.map